import cx from "clsx";
import { useScrollBoxApi } from "common-utils/hooks/useScrollBoxApi";
import { User } from "kreate-common/modules/business-types";
import ButtonPrimary from "kreate-common/modules/kreate-ui/components/ButtonPrimary";
import CustomButton from "kreate-common/modules/kreate-ui/components/CustomButton";
import Flex from "kreate-common/modules/kreate-ui/components/Flex";
import { useRouter } from "next/router";
import React from "react";
import {
  MdOutlineArrowBackIos,
  MdOutlineArrowForwardIos,
  MdPhotoSizeSelectActual,
} from "react-icons/md";

import CardCollection from "./components/CardCollection";
import styles from "./index.module.scss";

import { useLoginStatus } from "@/modules/common-hooks/useLoginStatus";
import PageLayout from "@/modules/kreate-components/components/PageLayout";

const SCROLL_BY_RATIO = 0.5;

type Props = {
  className?: string;
  style?: React.CSSProperties;
  user: User | null | undefined;
};

const ButtonArrow = CustomButton.withDefaultProps({
  className: styles.buttonArrow,
  size: "inherit",
  color: "inherit",
  padding: "minimal",
});

export default function SectionCollections({ className, style, user }: Props) {
  const router = useRouter();
  const loginStatus = useLoginStatus();
  const isOwner = loginStatus?.loggedIn && loginStatus.userId === user?.userId;
  const [scrollBox, setScrollBox] = React.useState<HTMLDivElement | null>(null);

  const scrollBoxApi = useScrollBoxApi(scrollBox);
  const shouldHideArrowButtons =
    !scrollBoxApi || scrollBoxApi.scrollWidth <= scrollBoxApi.clientWidth;

  if (!user || (user.collections.length === 0 && !isOwner)) {
    return null;
  }

  return (
    <div className={cx(styles.container, className)} style={style}>
      <Flex.Col gap="32px">
        <PageLayout.Section>
          <h2 className={styles.heading}>{"Collections"}</h2>
        </PageLayout.Section>

        {!user.collections.length && isOwner ? (
          <PageLayout.Section>
            <div className={styles.sectionCollectionDescription}>
              {"Showcase your unique collections here to share "}
              {"stories and mint on Blockchain."}
            </div>
            <Flex.Cell>
              <ButtonPrimary
                size="xl"
                icon={<MdPhotoSizeSelectActual />}
                content="Create a Collection"
                onClick={async () => await router.push("/new/collection")}
              />
            </Flex.Cell>
          </PageLayout.Section>
        ) : null}

        {user.collections.length ? (
          <div className={styles.scrollBoxContainer}>
            <div ref={setScrollBox} className={styles.scrollBox}>
              <div className={styles.scrollBoxContent}>
                {user.collections.length
                  ? user.collections.map((collection) => (
                      <div
                        key={collection.collectionId}
                        className={styles.cardCollection}
                      >
                        <CardCollection collection={collection} fill />
                      </div>
                    ))
                  : null}
              </div>
            </div>

            {!shouldHideArrowButtons ? (
              <ButtonArrow
                className={styles.buttonArrowLeft}
                icon={<MdOutlineArrowBackIos />}
                onClick={() =>
                  scrollBoxApi?.scrollLeft?.(
                    scrollBoxApi.clientWidth * SCROLL_BY_RATIO
                  )
                }
                disabled={!scrollBoxApi?.scrollLeft}
              />
            ) : undefined}

            {!shouldHideArrowButtons ? (
              <ButtonArrow
                className={styles.buttonArrowRight}
                icon={<MdOutlineArrowForwardIos />}
                onClick={() =>
                  scrollBoxApi?.scrollRight?.(
                    scrollBoxApi.clientWidth * SCROLL_BY_RATIO
                  )
                }
                disabled={!scrollBoxApi?.scrollRight}
              />
            ) : undefined}
          </div>
        ) : null}
      </Flex.Col>
    </div>
  );
}
